import { useNavigate } from "react-router-dom";
import { ChevronLeft, Printer, MoonStar } from "lucide-react";
import TrackerLogger from "@/components/TrackerLogger";
import { TRACKERS } from "@/lib/trackers";

const ROWS = Array.from({ length: 7 });
const LEVELS = ["Mild", "Medium", "Strong"];
const STRATEGIES = [
  "Choose breathable, moisture wicking bedding to maintain a comfortable temperature.",
  "Choose breathable sleepwear that allows air circulation.",
  "Have a fan or a damp cloth on hand to help cool down as needed.",
  "Drink water before bed and keep a glass within reach for easy access during the night.",
  "Avoid potential triggers like spicy foods or alcohol before bedtime.",
  "Maintain a cool bedroom temperature with proper ventilation.",
];

export default function SleepJournal() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-app" data-testid="sleep-journal-page">
      <div className="app-frame px-5 pt-6 pb-16 print-area">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-ink-muted mb-4 no-print" data-testid="sleep-journal-back">
          <ChevronLeft size={20} /> Back
        </button>

        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-sage flex items-center justify-center mx-auto">
            <MoonStar size={24} className="text-brand" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-ink mt-4">Weekly Sleep Quality Journal</h1>
          <p className="text-ink-soft text-sm mt-2 max-w-md mx-auto">
            Track your nighttime experiences to better understand your sleep patterns and develop effective coping strategies.
          </p>
        </div>

        <div className="mt-6">
          <p className="overline-label">Monitor your sleep patterns</p>
          <p className="text-ink-soft text-sm mt-2">Document the timing and intensity of night sweats to identify potential triggers and track improvement.</p>
        </div>

        <div className="mt-5 no-print">
          <TrackerLogger cfg={TRACKERS["weekly-sleep-quality-journal"]} />
        </div>

        <p className="overline-label mt-8 no-print">Prefer paper?</p>
        <p className="text-ink-soft text-sm mt-1 mb-3 no-print">Print a blank copy to fill in by hand.</p>

        <div className="mt-5 rounded-2xl border-2 border-[var(--brand)] overflow-hidden">
          <div className="grid grid-cols-12 bg-sage px-3 py-3">
            <div className="col-span-4 font-semibold text-ink text-sm">When</div>
            <div className="col-span-3 font-semibold text-ink text-sm">How strong</div>
            <div className="col-span-5 font-semibold text-ink text-sm">Sleep impact (hours &amp; quality)</div>
          </div>
          {ROWS.map((_, r) => (
            <div key={r} className={`grid grid-cols-12 px-3 py-4 ${r % 2 ? "bg-app" : "bg-white"} border-t border-subtle`} data-testid={`sleep-row-${r}`}>
              <div className="col-span-4 pr-2 space-y-3 text-sm text-ink-soft">
                <div>Date: <span className="inline-block border-b border-[var(--surface-accent)] w-16 sm:w-24 align-bottom" /></div>
                <div>Time: <span className="inline-block border-b border-[var(--surface-accent)] w-16 sm:w-24 align-bottom" /></div>
              </div>
              <div className="col-span-3 space-y-2">
                {LEVELS.map((lvl) => (
                  <div key={lvl} className="flex items-center gap-2 text-sm text-ink-soft">
                    <span className="w-4 h-4 rounded border-2 border-[var(--brand)] flex-shrink-0" />
                    {lvl}
                  </div>
                ))}
              </div>
              <div className="col-span-5">
                <div className="h-full min-h-[72px] rounded-lg border border-[var(--surface-accent)]" />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-subtle p-5 bg-white card-shadow">
          <h2 className="font-serif text-lg font-bold text-ink">Nighttime Comfort Strategies</h2>
          <p className="text-ink-soft text-sm mt-1">Track which methods help improve your sleep quality. Note the most effective combinations for you.</p>
          <ul className="mt-4 space-y-2.5">
            {STRATEGIES.map((s, i) => (
              <li key={i} className="flex gap-2.5 text-ink-soft text-sm" data-testid={`sleep-strategy-${i}`}>
                <span className="w-4 h-4 rounded-[4px] border-2 border-[var(--brand)] flex-shrink-0 mt-0.5" />
                <span>{s}</span>
              </li>
            ))}
          </ul>
          <p className="text-ink-soft text-sm italic mt-4">
            <span className="font-semibold text-ink not-italic">Strategy Tip:</span> Experiment with different combinations of these strategies to find your optimal nighttime comfort routine.
          </p>
        </div>

        <button
          data-testid="sleep-journal-print"
          onClick={() => window.print()}
          className="mt-6 w-full bg-brand hover:bg-[var(--brand-hover)] text-white font-semibold py-3.5 rounded-full flex items-center justify-center gap-2 no-print transition-colors"
        >
          <Printer size={16} /> Print / Download journal
        </button>
        <p className="text-ink-muted text-xs mt-3 text-center no-print">A self tracking tool for your own notes, not a medical record.</p>
      </div>
    </div>
  );
}
